import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Flex, message, Spin } from "antd";
import { LoadingOutlined, WarningFilled } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

import APIService from "../../Service/APIService";
import Button from "../../common/button/button";
import DropdownComponent from "../../common/dropdown-input";
import ModalView from "../../common/modal";
import TextInput from "../../common/text-input";
import TitleBar from "../../common/title-bar/title-bar";
import Text from "../../common/typography/text";
import { colors } from "../../theme/colors";
import { useAuth } from "../AuthProvider";
import {
  buildProductGroupDetailRequest,
  hasCompleteFedexMetadata,
  normalizeProductGroupDraft,
} from "./productGroupDetailsUtils";
import OpenOrderImageUpload from "./OpenOrderImageUpload";
import "./ProductGroupDetails.css";

const SHIPPING_OPTIONS = [
  { label: "Standard Shipping Rates", value: "standard" },
  { label: "Custom Shipping Rates", value: "custom" },
  { label: "Fedex Shipping Rates", value: "fedex" },
];

const PRICING_OPTIONS = [
  { label: "Rate Based Pricing", value: "rate" },
  { label: "Custom Pricing", value: "custom" },
];

const EMPTY_PRODUCT = {
  ProductName: "",
  ProductCode: "",
  Units: "",
  StandardPrice: "",
  Weight: "",
  OneRateXS: "",
  OneRateS: "",
  OneRateM: "",
  OneRateL: "",
  OneRateXL: "",
  CustomPackaging: false,
  CustomBoxFits: "",
  CustomBoxWidth: "",
  CustomBoxLength: "",
  CustomBoxHeight: "",
  ManageStock: false,
  StockQty: "",
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.data ||
  error?.message ||
  fallback;

const isPositive = (value) => Number.isFinite(Number(value)) && Number(value) > 0;

const normalizeStockBoolean = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const normalizeStockQty = (value) => {
  const parsed = Number(value ?? 0);
  return Number.isInteger(parsed) ? parsed : 0;
};

const BooleanToggle = ({ label, value, onChange, disabled = false }) => (
  <label className={`pg-toggle-card ${value ? "active" : ""} ${disabled ? "disabled" : ""}`}>
    <span>{label}</span>
    <input
      aria-label={label}
      checked={value}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      type="checkbox"
    />
    <span className="pg-toggle-track" aria-hidden="true">
      <span className="pg-toggle-thumb">{value ? "✓" : "×"}</span>
    </span>
  </label>
);

const SegmentedControl = ({ options, value, onChange }) => (
  <div className="pg-segmented" role="tablist">
    {options.map((option) => (
      <button
        aria-selected={value === option.value}
        className={value === option.value ? "active" : ""}
        key={option.value}
        onClick={() => onChange(option.value)}
        role="tab"
        type="button"
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const ProductSizeModal = ({ group, manufacturerID, product, onClose, onSaved }) => {
  const [draft, setDraft] = useState(() => ({ ...EMPTY_PRODUCT, ...(product || {}) }));
  const [applyToAll, setApplyToAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const isEdit = Boolean(product?.ID);
  const isSubmitting = saving || deleting;
  const isFedex = group?.ShippingType === "fedex";
  const rateBased = group?.PricingType !== "custom";

  useEffect(() => {
    if (rateBased && draft.Units !== "") {
      const calculated = Number(group?.Rate || 0) * Number(draft.Units || 0);
      setDraft((current) => ({ ...current, StandardPrice: Number.isFinite(calculated) ? calculated : "" }));
    }
  }, [draft.Units, group?.Rate, rateBased]);

  const setField = (field, value) => setDraft((current) => ({ ...current, [field]: value }));

  const validate = () => {
    if (!draft.ProductName.trim() || !draft.ProductCode.trim()) return "Product Name and Product Code are required.";
    if (!isPositive(draft.Units)) return "Units must be greater than 0.";
    if (!rateBased && (!Number.isFinite(Number(draft.StandardPrice)) || Number(draft.StandardPrice) < 0)) {
      return "Standard Price must be greater than or equal to 0.";
    }
    if (draft.ManageStock) {
      const stockQty = Number(draft.StockQty);
      if (draft.StockQty === "" || !Number.isInteger(stockQty) || stockQty < 0) {
        return "Available Stock must be a whole number greater than or equal to 0.";
      }
    }
    if (isFedex && !hasCompleteFedexMetadata(draft)) {
      return draft.CustomPackaging
        ? "Enter a positive weight, box dimensions, and product quantity."
        : "Enter a positive weight and nonnegative whole-number capacity for every FedEx box size.";
    }
    return "";
  };

  const save = async () => {
    if (isSubmitting) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const service = new APIService();
      const payload = { ...draft, ManufacturerID: manufacturerID, applyFedexToAll: applyToAll };
      const stockChanged = isEdit && (
        normalizeStockBoolean(product?.ManageStock) !== normalizeStockBoolean(draft.ManageStock) ||
        normalizeStockQty(product?.StockQty) !== normalizeStockQty(draft.StockQty)
      );
      const response = isEdit
        ? await service.patchDataToBackend(payload, `products/${product.ID}`)
        : await service.addDataToBackend(payload, `product-groups/${group.ID}/products`);
      onSaved(response?.data?.data?.Products || []);
      if (stockChanged) message.success("Stock updated successfully.");
      onClose();
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Unable to save this product size."));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async () => {
    if (!isEdit || isSubmitting) return;
    const result = await Swal.fire({
      title: "Delete product size?",
      text: `This will remove ${product.ProductCode || product.ProductName} from this product group and make it unavailable to linked accounts. Existing order history will be preserved.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: colors.failureColor,
      cancelButtonColor: colors.strokeDark,
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    setDeleting(true);
    setError("");
    try {
      const service = new APIService();
      const response = await service.deleteDataFromBackend(
        `products/${encodeURIComponent(product.ID)}?manufacturerId=${encodeURIComponent(manufacturerID)}`,
      );
      onSaved(response?.data?.data?.Products || []);
      message.success("Product size deleted.");
      onClose();
    } catch (deleteError) {
      setError(getErrorMessage(deleteError, "Unable to delete this product size."));
    } finally {
      setDeleting(false);
    }
  };

  const field = (name, title, placeholder, options = {}) => (
    <TextInput
      isRequired
      parentClassName={options.className || ""}
      titleText={title}
      placeholder={placeholder}
      type={options.type || "text"}
      value={draft[name]}
      isDisabled={options.disabled}
      setValue={(value) => setField(name, value)}
    />
  );

  return (
    <ModalView
      isOpen
      isLoading={isSubmitting}
      maxWidth="600px"
      minWidth="0"
      modalTitle={isEdit ? "Edit Product Size" : "Add Product Size"}
      onClose={isSubmitting ? () => {} : onClose}
      parentClassname="product-size-modal"
      textInputComponent={
        <div className="product-size-form">
          {error ? <Alert message={error} showIcon type="error" /> : null}
          <div className="product-base-grid">
            {field("ProductName", "Product Name", "Enter Product Name", { className: "product-name-field" })}
            {field("ProductCode", "Product Code", "Code")}
            {field("Units", "Units", "Units", { type: "number" })}
            {field("StandardPrice", "Standard Price", "$", { type: "number", disabled: rateBased })}
          </div>

          <div className="product-stock-grid">
            <BooleanToggle
              label="Manage Stock"
              onChange={(value) => setField("ManageStock", value)}
              value={Boolean(draft.ManageStock)}
            />
            <TextInput
              isRequired
              parentClassName="available-stock-field"
              className={!draft.ManageStock ? "disabled-field" : ""}
              titleText="Available Stock"
              placeholder="Stock Qty"
              type="number"
              value={draft.StockQty}
              isDisabled={!draft.ManageStock}
              setValue={(value) => setField("StockQty", value)}
            />
          </div>

          {isFedex ? (
            <>
              <SegmentedControl
                options={[
                  { label: "Fedex Standard Box", value: "standard" },
                  { label: "Custom Box", value: "custom" },
                ]}
                value={draft.CustomPackaging ? "custom" : "standard"}
                onChange={(value) => setField("CustomPackaging", value === "custom")}
              />
              {draft.CustomPackaging ? (
                <div className="fedex-section">
                  <Text variant="tinyText">Enter the details of the custom box</Text>
                  <div className="fedex-custom-grid">
                    {field("Weight", "Product Weight (lbs)", "Lbs", { type: "number" })}
                    {field("CustomBoxWidth", "Box Width (in)", "In", { type: "number" })}
                    {field("CustomBoxLength", "Box Length (in)", "In", { type: "number" })}
                    {field("CustomBoxHeight", "Box Height (in)", "In", { type: "number" })}
                    {field("CustomBoxFits", "Product Qty", "#", { type: "number" })}
                  </div>
                </div>
              ) : (
                <div className="fedex-section">
                  <Text variant="tinyText">Enter quantity of product that would fit in each of the Fedex Standard Size boxes below:</Text>
                  <div className="fedex-standard-grid">
                    {field("Weight", "Product Weight (lbs)", "Lbs", { type: "number" })}
                    {field("OneRateXS", "XS", "#", { type: "number" })}
                    {field("OneRateS", "S", "#", { type: "number" })}
                    {field("OneRateM", "M", "#", { type: "number" })}
                    {field("OneRateL", "L", "#", { type: "number" })}
                    {field("OneRateXL", "XL", "#", { type: "number" })}
                  </div>
                </div>
              )}
              <label className="apply-all-checkbox">
                <input checked={applyToAll} onChange={(event) => setApplyToAll(event.target.checked)} type="checkbox" />
                <span>Apply to all product sizes</span>
              </label>
            </>
          ) : null}
        </div>
      }
      footerButtonWrapper={
        <div className="product-modal-actions">
          {isEdit ? (
            <Button disabled={isSubmitting} onClick={deleteProduct} subVariant="failure" variant="table">
              {deleting ? "Deleting…" : "Delete Product"}
            </Button>
          ) : null}
          <div className="product-modal-primary-actions">
            <Button disabled={isSubmitting} onClick={onClose} variant="table">Cancel</Button>
            <Button disabled={isSubmitting} onClick={save} subVariant="success" variant="table">
              {saving ? "Saving…" : "Save Product"}
            </Button>
          </div>
        </div>
      }
    />
  );
};

const ProductGroupDetails = () => {
  const { manufacturerId, productGroupId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [manufacturer, setManufacturer] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [draft, setDraft] = useState(null);
  const [products, setProducts] = useState([]);
  const [standardRates, setStandardRates] = useState([]);
  const [productGroupCategories, setProductGroupCategories] = useState([]);
  const [shippingCategories, setShippingCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingActiveStatus, setSavingActiveStatus] = useState(false);
  const [error, setError] = useState("");
  const [productModal, setProductModal] = useState(null);
  const [openOrderImageFile, setOpenOrderImageFile] = useState(null);

  const isAdmin = user?.Type === "Admin";
  const isDirty = useMemo(
    () => Boolean(
      openOrderImageFile ||
      (snapshot && draft && JSON.stringify(snapshot) !== JSON.stringify(draft))
    ),
    [draft, openOrderImageFile, snapshot],
  );

  const load = useCallback(async () => {
    if (!isAdmin) {
      setLoading(false);
      setError("Admin access is required to edit product groups.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const service = new APIService();
      const [
        manufacturerResponse,
        groupResponse,
        productGroupCategoriesResponse,
        shippingCategoriesResponse,
      ] = await Promise.all([
        service.getDataFromBackend(`getmanufacturerbyid?ID=${encodeURIComponent(manufacturerId)}`),
        service.getDataFromBackend(`product-groups/${encodeURIComponent(productGroupId)}?manufacturerId=${encodeURIComponent(manufacturerId)}`),
        service.getDataFromBackend("product-group-categories"),
        service.getDataFromBackend("getshippingcategoryoptions"),
      ]);
      const group = groupResponse?.data?.data?.ProductGroup;
      const loadedProductGroupCategories =
        productGroupCategoriesResponse?.data?.data?.ProductGroupCategories;
      if (
        !Array.isArray(loadedProductGroupCategories) ||
        loadedProductGroupCategories.length === 0
      ) {
        throw new Error("No Product Group categories are available.");
      }
      const normalized = normalizeProductGroupDraft(group);
      if (
        normalized.ProductGroupCategoryID &&
        !loadedProductGroupCategories.some(
          (category) =>
            Number(category?.ID) === normalized.ProductGroupCategoryID,
        )
      ) {
        throw new Error(
          "The saved Product Group category is no longer available.",
        );
      }
      setManufacturer(manufacturerResponse?.data?.data || null);
      setSnapshot(normalized);
      setDraft(normalized);
      setOpenOrderImageFile(null);
      setProducts(groupResponse?.data?.data?.Products || []);
      setStandardRates(groupResponse?.data?.data?.StandardShippingRates || []);
      setProductGroupCategories(loadedProductGroupCategories);
      setShippingCategories(shippingCategoriesResponse?.data?.data || []);
    } catch (loadError) {
      const status = loadError?.response?.status;
      setError(
        status === 403
          ? "You do not have permission to edit this product group."
          : status === 404
            ? "This product group could not be found for the selected manufacturer."
            : getErrorMessage(loadError, "Unable to load the product group."),
      );
    } finally {
      setLoading(false);
    }
  }, [isAdmin, manufacturerId, productGroupId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const beforeUnload = (event) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const protectLinks = (event) => {
      if (!isDirty) return;
      const anchor = event.target.closest?.("a[href]");
      if (anchor && !window.confirm("Discard unsaved product group changes?")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", protectLinks, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", protectLinks, true);
    };
  }, [isDirty]);

  const update = (field, value) => setDraft((current) => ({ ...current, [field]: value }));
  const productGroupCategoryOptions = productGroupCategories.map((category) => ({
    label: category.Category,
    value: Number(category.ID),
  }));
  const selectedProductGroupCategory =
    productGroupCategoryOptions.find(
      (option) => option.value === draft?.ProductGroupCategoryID,
    ) || null;
  const shippingOptions = shippingCategories.map((category) => ({
    label: category.ShippingCategoryName,
    value: category.ID,
  }));
  const selectedShippingCategory = shippingOptions.find((option) => option.value === draft?.ShippingCategoryID) || null;
  const selectedCategoryRates = shippingCategories.find((category) => Number(category.ID) === Number(draft?.ShippingCategoryID))?.ShippingRates || [];

  const saveGroup = async () => {
    if (!draft.ProductGroupName.trim()) {
      setError("Product Group Name is required.");
      return;
    }
    if (!draft.ProductGroupCategoryID) {
      setError("Product Group Category is required.");
      return;
    }
    if (draft.InvoicePrefix.length > 200) {
      setError("Invoice Prefix must be 200 characters or fewer.");
      return;
    }
    if (draft.EnableOpenOrder && !draft.CollectPayment) {
      setError("Collect Payment must be enabled before Allow Open Order.");
      return;
    }
    if (draft.ShippingType === "custom" && !draft.ShippingCategoryID) {
      setError("Choose a Shipping Category for Custom Shipping Rates.");
      return;
    }
    if (draft.PricingType === "rate" && (!Number.isFinite(Number(draft.Rate)) || Number(draft.Rate) < 0)) {
      setError("Product Group Rate must be greater than or equal to 0.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const service = new APIService();
      const payload = {
        ...draft,
        ManufacturerID: Number(manufacturerId),
        CustomShipping: draft.ShippingType === "custom",
        UseFedexShipping: draft.ShippingType === "fedex",
      };
      const { body: requestBody, config: requestConfig } = buildProductGroupDetailRequest(
        payload,
        openOrderImageFile,
      );
      const response = await service.patchDataToBackend(
        requestBody,
        `product-groups/${productGroupId}`,
        requestConfig,
      );
      const normalized = normalizeProductGroupDraft(response?.data?.data?.ProductGroup);
      setSnapshot(normalized);
      setDraft(normalized);
      setOpenOrderImageFile(null);
      if (response?.data?.data?.Products) setProducts(response.data.data.Products);
    } catch (saveError) {
      setError(getErrorMessage(saveError, "Unable to save product group changes."));
    } finally {
      setSaving(false);
    }
  };

  const saveActiveStatus = async (isActive) => {
    const previousActive = Boolean(draft?.IsActive);
    setSavingActiveStatus(true);
    setError("");
    setDraft((current) => current ? { ...current, IsActive: isActive } : current);
    try {
      const service = new APIService();
      const response = await service.patchDataToBackend(
        {
          ManufacturerID: Number(manufacturerId),
          IsActive: isActive,
        },
        `product-groups/${productGroupId}/active`,
      );
      const returnedActive = response?.data?.data?.ProductGroup?.IsActive;
      const nextActive = returnedActive === undefined ? isActive : Boolean(returnedActive);
      setDraft((current) => current ? { ...current, IsActive: nextActive } : current);
      setSnapshot((current) => current ? { ...current, IsActive: nextActive } : current);
    } catch (activeStatusError) {
      setDraft((current) => current ? { ...current, IsActive: previousActive } : current);
      setError(getErrorMessage(activeStatusError, "Unable to update product group active status."));
    } finally {
      setSavingActiveStatus(false);
    }
  };

  const goBack = () => {
    if (!isDirty || window.confirm("Discard unsaved product group changes?")) {
      navigate(`/Manufacturer/${manufacturerId}?tab=details`);
    }
  };

  const openProductModal = (product) => {
    if (isDirty) {
      setError("Save or discard product group changes before editing product sizes.");
      return;
    }
    setProductModal(product);
  };

  if (loading) {
    return <Flex align="center" justify="center" className="pg-page-state"><Spin indicator={<LoadingOutlined spin />} size="large" /></Flex>;
  }
  if (!draft || (error && !snapshot)) {
    return (
      <div className="pg-page-state">
        <Alert message={error || "Product group unavailable."} showIcon type="error" />
        <div className="pg-state-actions"><Button onClick={load} variant="table" subVariant="primary">Retry</Button><Button onClick={goBack} variant="table">Back</Button></div>
      </div>
    );
  }

  return (
    <div className="product-group-page">
      {saving ? <Spin fullscreen indicator={<LoadingOutlined spin />} /> : null}
      <TitleBar
        entityName={manufacturer?.ManufacturerName || "Manufacturer"}
        entityNameLink={`/Manufacturer/${manufacturerId}?tab=details`}
        label={draft.ProductGroupName || "Product Group"}
        titleBarLeftExtra={
          <BooleanToggle label="Active" disabled={saving || savingActiveStatus} onChange={saveActiveStatus} value={draft.IsActive} />
        }
        rightFilterBarComponents={isDirty ? (
          <div className="pg-title-actions">
            <Button onClick={() => { setDraft(snapshot); setOpenOrderImageFile(null); setError(""); }} variant="table">Discard Changes</Button>
            <Button disabled={saving} onClick={saveGroup} subVariant="success" variant="table">Save Changes</Button>
          </div>
        ) : null}
      />
      {error ? <Alert closable message={error} onClose={() => setError("")} showIcon type="error" /> : null}

      <div className="pg-content-grid">
        <section className="pg-details-section">
          <Text variant="bodyText">Product Group Details</Text>
          <div className="pg-field-grid">
            <TextInput isRequired parentClassName="pg-name-field" placeholder="Enter Product Group Name" setValue={(value) => update("ProductGroupName", value)} titleText="Product Group Name" value={draft.ProductGroupName} />
            <TextInput placeholder="HCPCS" setValue={(value) => update("QCode", value)} titleText="HCPCS Code" value={draft.QCode} />
            <TextInput placeholder="Enter Prefix" setValue={(value) => update("InvoicePrefix", value.slice(0, 200))} titleText="Invoice Prefix" value={draft.InvoicePrefix} />
          </div>
          <DropdownComponent
            isDisabled={saving}
            isRequired
            label="Choose Product Group Category"
            options={productGroupCategoryOptions}
            parentClassname="pg-category-field"
            placeholder="Choose a product group category from the list below"
            setValue={(option) =>
              update("ProductGroupCategoryID", option?.value || null)
            }
            value={selectedProductGroupCategory}
          />

          <div className="pg-toggle-grid">
            <BooleanToggle label="Is Serialized" onChange={(value) => update("Serialized", value)} value={draft.Serialized} />
            <BooleanToggle label="Requires IVR" onChange={(value) => update("IVR", value)} value={draft.IVR} />
            <BooleanToggle label="Requires Procedure Info" onChange={(value) => update("RequireProcedureInfo", value)} value={draft.RequireProcedureInfo} />
            <BooleanToggle label="Signature Required for Delivery" onChange={(value) => update("SignatureRequiredForDelivery", value)} value={draft.SignatureRequiredForDelivery} />
            <BooleanToggle label="Collect Payment" onChange={(value) => update("CollectPayment", value)} value={draft.CollectPayment} />
            <BooleanToggle label="Allow Open Order" onChange={(value) => update("EnableOpenOrder", value)} value={draft.EnableOpenOrder} />
          </div>

          <OpenOrderImageUpload
            currentFileName={draft.OpenOrderImageFileName}
            disabled={saving}
            onClear={() => setOpenOrderImageFile(null)}
            onFileSelect={setOpenOrderImageFile}
            selectedFile={openOrderImageFile}
          />

          <SegmentedControl onChange={(value) => update("ShippingType", value)} options={SHIPPING_OPTIONS} value={draft.ShippingType} />
          <div className="pg-helper-content">
            {draft.ShippingType === "standard" ? (
              <><Text variant="tinyText">The following standard shipping rates will be charged:</Text><ul>{standardRates.map((rate) => <li key={rate.optionCode}>{rate.optionCode}</li>)}</ul></>
            ) : null}
            {draft.ShippingType === "custom" ? (
              <DropdownComponent isRequired label="Choose a Shipping Category for Custom Shipping Rates" options={shippingOptions} parentClassname="pg-category-field" placeholder="Select a shipping category from list" setValue={(option) => update("ShippingCategoryID", option?.value || null)} value={selectedShippingCategory} />
            ) : null}
            {draft.ShippingType === "custom" && selectedCategoryRates.length ? <ul>{selectedCategoryRates.map((rate) => <li key={rate.ID}>{rate.ShippingRateName} - ${Number(rate.ShippingRate).toLocaleString()}</li>)}</ul> : null}
            {draft.ShippingType === "fedex" ? <Text variant="tinyText">Realtime Fedex rates will be fetched. Please enter the required details for each size.</Text> : null}
          </div>

          <SegmentedControl onChange={(value) => update("PricingType", value)} options={PRICING_OPTIONS} value={draft.PricingType} />
          <div className="pricing-copy">
            {draft.PricingType === "rate" ? <Text variant="tinyText">The standard price will be calculated based on each product size units times the product group rate below.</Text> : null}
            {draft.PricingType === "custom" ? <Text variant="tinyText">A custom standard price can be set for each product size in this product group.</Text> : null}
          </div>
          {draft.PricingType === "rate" ? <TextInput isRequired parentClassName="pg-rate-field" placeholder="0.00" setValue={(value) => update("Rate", value)} titleText="Enter Product Group Rate" type="number" value={draft.Rate} /> : null}
        </section>

        <section className="pg-products-section">
          <div className="pg-products-header"><Text variant="bodyText">Product Sizes</Text><Button onClick={() => openProductModal(EMPTY_PRODUCT)} variant="table" subVariant="primary">Add New Size</Button></div>
          <div className="pg-product-list">
            {products.length ? products.map((product) => {
              const showWarning = draft.ShippingType === "fedex" && !hasCompleteFedexMetadata(product);
              return (
                <button className="pg-product-card" key={product.ID} onClick={() => openProductModal(product)} type="button">
                  <div className="pg-product-main"><Text variant="tinyText">{product.ProductName}</Text><div className="pg-product-code">{showWarning ? <WarningFilled aria-label="Missing FedEx details" /> : null}<Text color="lightText" variant="tinyText">{product.ProductCode}</Text></div></div>
                  <div className="pg-product-price"><Text color="lightText" variant="tinyText">Units {product.Units}</Text><Text variant="bodyText">${Number(product.StandardPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></div>
                </button>
              );
            }) : <div className="pg-empty-products"><Text variant="bodyText">No product sizes have been added.</Text></div>}
          </div>
        </section>
      </div>

      {productModal ? <ProductSizeModal group={{ ...draft, ID: Number(productGroupId) }} manufacturerID={Number(manufacturerId)} onClose={() => setProductModal(null)} onSaved={setProducts} product={productModal.ID ? productModal : null} /> : null}
    </div>
  );
};

export default ProductGroupDetails;
